import { alpha } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';

const controlBase = (theme: Theme) => ({
  backgroundColor: theme.palette.background.default,
  borderRadius: '999px',
  boxShadow: theme.elevations.card,
  height: { xs: 40, sm: 44 },
  display: 'inline-flex',
  alignItems: 'center',
});

export const mapControlsStyles: Record<string, SxProps<Theme>> = {
  // On mobile the three pills (level/age/amount) must stay on one line
  // (the Figma mobile frames show them as a single row) — `nowrap` plus the
  // tighter gap/padding/font-size below keeps them from wrapping at 402px.
  root: (theme) => ({
    display: 'flex',
    flexWrap: { xs: 'nowrap', sm: 'wrap' },
    alignItems: 'center',
    gap: { xs: theme.spacing(1), sm: theme.spacing(1.5) },
  }),
  // Segmented control: solid white pill container, lavender highlight on the
  // active segment.
  toggleGroup: (theme) => ({
    backgroundColor: theme.palette.background.default,
    borderRadius: '999px',
    boxShadow: theme.elevations.card,
    border: `1px solid ${theme.palette.divider}`,
    height: 44,
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px',
    '& .MuiToggleButtonGroup-grouped': {
      border: 0,
      borderRadius: '999px !important',
      marginInline: '2px',
    },
  }),
  toggle: (theme) => ({
    textTransform: 'none',
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: '0.9375rem',
    color: theme.palette.primary.main,
    paddingInline: theme.spacing(2),
    height: 36,
    backgroundColor: 'transparent',
    '&.Mui-selected': {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightBold,
      '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.14) },
    },
  }),
  select: (theme) => ({
    ...controlBase(theme),
    paddingInline: { xs: theme.spacing(1.75), sm: theme.spacing(2.5) },
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
    color: theme.palette.text.primary,
    minWidth: { xs: 0, sm: 130 },
    flexShrink: { xs: 1, sm: 0 },
    '& .MuiSelect-select': {
      color: theme.palette.text.primary,
      padding: 0,
      paddingRight: { xs: '24px !important', sm: '28px !important' },
      display: 'flex',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    '& .MuiSelect-select:focus': { backgroundColor: 'transparent' },
    '& .MuiSelect-icon': { color: theme.palette.text.primary, right: { xs: 8, sm: 12 } },
  }),
};
